import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  Paper,
  Alert,
  useTheme,
  Tooltip,
  IconButton,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ModalMAIQ from "app/components/ModalMAIQ";
import SelectMAIQ from "app/components/SelectMAIQ";
import { useRole } from "app/context/RoleContext";
import { useScope } from "app/context/ScopeContext";
import { useResource } from "app/context/ResourceContext";
import DualListMAIQ from 'app/components/DualListMAIQ';
import { Role, Scope, Resource } from '../../types';

interface AddRoleModalProps {
  open: boolean;
  onClose: () => void;
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>;
}

export const AddRoleModal: React.FC<AddRoleModalProps> = ({ open, onClose, setRefreshKey }) => {
  const { addRole } = useRole();
  const { scopes, getAllScopes } = useScope();
  const { resources: contextResources, getAllResources } = useResource();
  const resources = Array.isArray(contextResources) ? contextResources : [];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>("");
  const theme = useTheme();
  const [formData, setFormData] = useState<{
    name: string;
    scopes: string[];
    connected_resources: string[];
    group_can_be_attached: boolean;
    list_can_be_attached: boolean;
    access_to_all_dealership: boolean;
    old_layout: boolean;
  }>({
    name: '',
    scopes: [],
    connected_resources: [],
    group_can_be_attached: false,
    list_can_be_attached: false,
    access_to_all_dealership: false,
    old_layout: false,
  });

  useEffect(() => {
    if (open) {
      getAllScopes();
      getAllResources();
    }
  }, [open]);

  useEffect(() => {
    if (scopes && scopes.length > 0) {
      const allScope = scopes.find(scope => scope.scope === "all");
      if (allScope) {
        setFormData(prev => ({
          ...prev,
          scopes: [allScope._id]
        }));
      }
    }
  }, [scopes]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError("");
  };

  const handleScopeChange = (event: any) => {
    setFormData(prev => ({ ...prev, scopes: event.target.value }));
    setError("");
  };

  const convertedResources = (resourceObjects: Resource[]) => {
    return resourceObjects.map(r => ({
      id: r._id ?? '',
      label: r.component || r.name || '—',
      secondaryLabel: r.path || undefined,
    }));
  };

  const handleConnectedResourcesUpdate = (updatedResources: { id: string; label: string }[]) => {
    setFormData(prev => ({ ...prev, connected_resources: updatedResources.map(r => r.id) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.scopes || formData.scopes.length === 0) {
      setError("Please select at least one scope");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await addRole(formData);
      // Increment the refreshKey to trigger a re-render in the parent component
      setRefreshKey((prevKey) => prevKey + 1);
      onClose();
      // Reset form
      setFormData({
        name: '',
        scopes: [],
        connected_resources: [],
        group_can_be_attached: false,
        list_can_be_attached: false,
        access_to_all_dealership: false,
        old_layout: false,
      });
    } catch (err) {
      console.error("Error adding role:", err);
      setError(err instanceof Error ? err.message : 'Failed to submit form');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalMAIQ 
      open={open} 
      onClose={onClose}
      hideDefaultCloseButton={true}
    >
      <Paper
        elevation={0}
        sx={{
          width: 600,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          sx={{
            p: 2,
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleAltIcon fontSize="small" />
              <Typography variant="h6" fontWeight="600">
                Add New Role
              </Typography>
            </Box>
            <Box>
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    color: 'white',
                    p: 0.5,
                  }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Scrollable content area */}
        <Box
          component="form"
          id="role-form"
          onSubmit={handleSubmit}
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
            py: 2,
            px: 3
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Role Name *
            </Typography>
            <TextField
              fullWidth
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter role name"
              inputProps={{
                sx: { py: 1.5 }
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Scopes *
            </Typography>
            <Box sx={{ 
              '& .MuiOutlinedInput-root': {
                padding: '8px 14px',
              },
              '& .MuiInputLabel-root': {
                transform: 'translate(14px, 16px) scale(1)',
              },
              '& .MuiInputLabel-shrink': {
                transform: 'translate(14px, -6px) scale(0.75)',
              },
              '& .MuiFormControl-root': {
                marginTop: 0
              }
            }}>
              <SelectMAIQ
                label="Select scopes for this role"
                value={formData.scopes}
                options={scopes || []}
                onChange={handleScopeChange}
                isMulti={true}
                disabled={isSubmitting}
                renderValue="name"
                selectedValue="_id"
              />
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="group_can_be_attached"
                  checked={formData.group_can_be_attached}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              }
              label="Group can be attached"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="list_can_be_attached"
                  checked={formData.list_can_be_attached}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              }
              label="List can be attached"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="access_to_all_dealership"
                  checked={formData.access_to_all_dealership}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              }
              label="Access to all dealership"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="old_layout"
                  checked={formData.old_layout}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              }
              label="Old layout"
            />
          </Box>
        </Box>

        {/* Footer with actions - fixed */}
        <Box 
          sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'flex-end',
            backgroundColor: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            zIndex: 9,
            flexShrink: 0,
            gap: 2
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 600,
              color: 'text.primary',
              borderColor: 'divider',
              py: 0.75
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            form="role-form"
            onClick={(e) => {
              // If form submission doesn't happen through the form's onSubmit,
              // trigger it manually here as a fallback
              handleSubmit(e as any);
            }}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 600, 
              bgcolor: theme.palette.primary.main,
              color: 'white',
              py: 0.75,
              px: 3,
              '&:hover': {
                bgcolor: '#0a1f3a',
              }
            }}
          >
            {isSubmitting ? 'Creating...' : 'Create Role'}
          </Button>
        </Box>
      </Paper>
    </ModalMAIQ>
  );
};

export default AddRoleModal; 